import './lib/monitoring'; // Initialize Sentry before anything else
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { initializeSecurity } from './utils/security';
import { initAnalytics } from './utils/analytics';
import { ThemeProvider } from './contexts/ThemeContext';
import { ContactFormProvider } from './contexts/ContactFormContext';
import ErrorBoundary from './components/ErrorBoundary';
import { shouldHydrateRoot } from './utils/rootRendering';

const isDev = import.meta.env.DEV;

if (isDev) {
  console.log('Application initializing...');

  // Capture uncaught errors in development only.
  window.addEventListener('error', event => {
    console.error('Global error caught:', event.error);
    console.error('Error message:', event.message);
    console.error('Error source:', event.filename, event.lineno, event.colno);
  });

  // Capture unhandled promise rejections in development only.
  window.addEventListener('unhandledrejection', event => {
    console.error('Unhandled Promise Rejection:', event.reason);
  });
}

// Debug element exists
const rootElement = document.getElementById('root');
if (isDev) {
  console.log('Root element exists:', !!rootElement);
}

// Set up security features before the app renders
try {
  if (isDev) {
    console.log('Initializing security...');
  }
  initializeSecurity();
  if (isDev) {
    console.log('Security initialized successfully');
  }
} catch (error) {
  console.error('Error initializing security:', error);
}

// Set up analytics tracking
try {
  if (isDev) {
    console.log('Initializing analytics...');
  }
  initAnalytics();
  if (isDev) {
    console.log('Analytics initialized successfully');
  }
} catch (error) {
  console.error('Error initializing analytics:', error);
}

// Wrap render in try-catch
try {
  if (!rootElement) {
    throw new Error('Root element not found in the DOM');
  }

  const appTree = (
    <ErrorBoundary>
      <ThemeProvider>
        <ContactFormProvider>
          <App />
        </ContactFormProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );

  // Static fallback content is replaced on mount; only app-rendered HTML
  // can be hydrated as the React tree.
  if (shouldHydrateRoot(rootElement)) {
    if (isDev) console.log('Hydrating prerendered HTML...');
    hydrateRoot(rootElement, appTree);
  } else {
    if (isDev) console.log('Creating React root + rendering...');
    createRoot(rootElement).render(appTree);
  }
  if (isDev) {
    console.log('Application rendered successfully');
  }
} catch (error) {
  console.error('Fatal error rendering application:', error);
}
